/**
 * Plans and billing states.
 *
 * Both are `text` + `CHECK` in Postgres rather than PG enums, applied uniformly
 * across every §4 enum (ruled 2026-08-04): `ALTER TYPE … ADD VALUE` cannot use the
 * new value in the same transaction, so add-and-backfill would need two migrations,
 * and an enum value can be renamed but never removed. The ordering advantage a PG
 * enum would buy does not apply — the ladder is compared here, in TypeScript, never
 * in SQL.
 *
 * That makes the `CHECK` constraint and these constants two copies of one fact, so
 * `packages/db` asserts set-equality against `information_schema` and the test is
 * probed by changing one side. Without it the decision would merely relocate the
 * drift somewhere it looks safer.
 */
/**
 * Subscription tiers, **cheapest first**. The free tier is named Launch, and
 * `launch` is the stored value — not a display alias for `free` (ruled 2026-08-03).
 *
 * The order is load-bearing, which is why {@link planRank} exists rather than
 * leaving it a comment: entitlement code asks `planRank(a) >= planRank(b)`, so this
 * array must stay a **monotonic ladder** where every value means "more than the one
 * before it".
 *
 * `agency` is deliberately absent (ruled 2026-08-04). An agency is a channel plus a
 * structure — an org with many workspaces and `memberships.workspace_ids` scoping,
 * which §4 already models — not a rung. A value meaning "different shape" sitting in
 * a field where every other value means "higher tier" breaks rank comparisons
 * *silently*, which is the worst way for a pricing bug to fail.
 */
export const TIERS = ["launch", "starter", "growth", "scale", "enterprise"];
/**
 * The tiers a customer can reach on their own.
 *
 * `enterprise` is set administratively after a sales conversation — there is no
 * Stripe checkout behind it. Any plan picker must render this list rather than
 * {@link TIERS}: showing Enterprise as selectable implies a self-serve path that
 * does not exist, and the first customer to click it has a bad time.
 */
export const SELF_SERVE_TIERS = TIERS.filter((t) => t !== "enterprise");
/**
 * Position on the pricing ladder, 0-based and cheapest-first.
 *
 * Compare ranks, never tier strings: `planRank(org.plan) >= planRank("growth")`.
 */
export function planRank(tier) {
    return TIERS.indexOf(tier);
}
/**
 * Billing states.
 *
 * These may surface on a consumer page at `warn` severity at most. `error` is
 * unavailable to billing by contract: a brand's unpaid invoice is not the scanning
 * consumer's problem, and a printed code must never present as broken because of it
 * (invariant 1, spec §3.6).
 */
export const BILLING_STATES = ["active", "grace", "degraded", "churned"];
//# sourceMappingURL=tiers.js.map